<template>
  <div id="attribution">
    <div class="audience-carriers_device">
      <div class="cuebiq-panel">
        <div class="cuebiq-panel__body cuebiq-panel__body--no-heading audience-insights-info-title">
            <p>The lookback period for the stats below is 90 days before the conversion window end date.</p>
        </div>
      </div>
      <div class="cuebiq-panel">
        <div class="cuebiq-panel__heading">
          <h3 class="cuebiq-panel__title">Audience Insights: Top Carriers and Device Models
            <info-tooltip text="This chart shows audience insights based on their mobile devices. Top carrier services and device models are distributed for both the exposed and converted users."/>
          </h3>
        </div>
        <div class="cuebiq-panel__body ">
          <div id="topLegend" style="width: 100%;">
            <ul class="labels-legend">
              <li>Exposed<span style="width: 15px; height: 15px; display: inline-block; background-color: rgb(61, 103, 154);"></span></li>
              <li>Converted<span style="width: 15px; height: 15px; display: inline-block; background-color: rgb(110, 188, 228);"></span></li>
            </ul>
          </div>
          <div class="row">
            <div class="audience-carriers col-md-6 col-xs-12">
              <div class="cuebiq-section">
                <h4 class="cuebiq-section__title">Top carriers
                  <info-tooltip text="Percentage represents the top carriers in both the exposed and converted devices."/>
                </h4>
                <div class="cuebiq-section__content">
                  <div class="col-xs-11">
                    <div class="recharts-responsive-container" style="width: 100%; height: 380px; position: relative;">
                      <div class="recharts-wrapper" style="position: relative; cursor: default; width: 501px; height: 380px;">
                        <hori-bar-chart v-if="top_carriers_chart.data" :chart-data="top_carriers_chart.data" :options="top_carriers_chart.options"></hori-bar-chart>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="audience-device col-md-6 col-xs-12">
              <div class="cuebiq-section">
                <h4 class="cuebiq-section__title">Top device models
                  <info-tooltip text="Percentage represents the top device models in both the exposed and converted devices."/>
                </h4>
                <div class="cuebiq-section__content">
                  <div class="col-xs-11">
                    <div class="recharts-responsive-container" style="width: 100%; height: 380px; position: relative;">
                      <div class="recharts-wrapper" style="position: relative; cursor: default; width: 501px; height: 380px;">
                        <hori-bar-chart v-if="top_device_models_chart.data" :chart-data="top_device_models_chart.data" :options="top_device_models_chart.options"></hori-bar-chart>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="cuebiq-separator">
    <div class="audience-geobehavioral-correllation cuebiq-panel">
      <div class="cuebiq-panel__heading">
        <h3 class="cuebiq-panel__title">Geo-behavioral audiences correlation
          <info-tooltip text="This page shows your audience's most common geo-behaviors and interests by category.<br/>For each geo-behavior your audience displays, we analyzed the degree of affinity and interest compared to the control group."/>
        </h3>
      </div>
      <div class="cuebiq-panel__body ">
        <div>
          <div class="row">
            <div style="text-align: center;">
              <div>
                <word-cloud
                  v-if="audience_correlation_word_cloud.data"
                  :width="800"
                  :height="300"
                  :data="audience_correlation_word_cloud.data"
                  :key-field="audience_correlation_word_cloud.keyField"
                  :value-field="audience_correlation_word_cloud.valueField"
                  @clicked="onWordCloudClick"></word-cloud>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="tooltip cuebiq"></div>
            <div class="col-xs-2">
              <div class="cuebiq-section">
                <h4 class="cuebiq-section__title" v-if="audience_chart.path.length">{{ audience_chart.path[0] }}</h4>
                <div class="cuebiq-section__content">
                  <div class="radial overlap clearfix">
                    <div id="audience-analysis-radial-overlap" class="cuebiq-radial undefined">
                      <vue-circle
                        ref="audienceInterestRateProgress"
                        :progress="0"
                        :size="100"
                        :reverse="false"
                        line-cap="round"
                        :fill="{ color: $chartSettings['progress_circle']['fill'] }"
                        empty-fill="rgba(0, 0, 0, .1)"
                        :animation-start-value="0.0"
                        :start-angle="-1.57"
                        insert-mode="append"
                        :thickness="$chartSettings['progress_circle']['thickness']"
                        :show-percent="false">
                      </vue-circle>
                    </div>
                    <div class="metric metric--numeric  ">
                      <div class="metric__content metric__content--numeric">
                        <h3 class="metric__title metric__title--numeric ">
                          <dc-number v-if="audience_interest_number_display" v-bind="audience_interest_number_display"></dc-number>
                        </h3>
                        <p class="metric__subtitle">Audience Interest
                          <info-tooltip text="The percentage represents how many of the analyzed IDs displays a certain geo-behavior."/>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="radial uplift clearfix">
                    <div id="audience-analysis-radial-uplift" class="cuebiq-radial undefined">
                      <vue-circle
                        ref="audienceUpliftRateProgress"
                        :progress="0"
                        :size="100"
                        :reverse="false"
                        line-cap="round"
                        :fill="{ color: $chartSettings['progress_circle']['fill'] }"
                        empty-fill="rgba(0, 0, 0, .1)"
                        :animation-start-value="0.0"
                        :start-angle="-1.57"
                        insert-mode="append"
                        :thickness="$chartSettings['progress_circle']['thickness']"
                        :show-percent="false">
                      </vue-circle>
                    </div>
                    <div class="metric metric--numeric  ">
                      <div class="metric__content metric__content--numeric">
                        <h3 class="metric__title metric__title--numeric ">
                          <dc-number v-if="audience_uplift_number_display" v-bind="audience_uplift_number_display"></dc-number>
                        </h3>
                        <p class="metric__subtitle">Audience Footfall Uplift
                          <info-tooltip text="Percentage variation between the exposed group users' visits and the control group users' visits, calculated only on the selected audience."/>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xs-8" style="height: 500px; text-align: right;">
              <div id="bar-chart-audience">
                <drilldown-hori-bar-chart
                  v-if="audience_chart.mappedTreeData && audience_chart.path.length"
                  :chart-data="audience_chart.mappedTreeData"
                  :path="audience_chart.path"
                  :options="audience_chart.options"
                  :height="485">
                </drilldown-hori-bar-chart>
              </div>
            </div>
            <div class="col-xs-2">
              <button
                type="button" class="tree-barchart-back-btn btn btn-primary"
                v-if="audience_chart.path.length > 1"
                v-on:click="popAudienceChartPath()">Back</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="cuebiq-separator">
    <div class="cuebiq-panel">
      <div class="cuebiq-panel__heading">
        <h3 class="cuebiq-panel__title">Brand affinity
          <info-tooltip text="This page shows your audience's most common geo-behaviors and interests by brand.<br/>For each brand, we analyzed the degree of affinity and interest compared to the general population."/>
        </h3>
      </div>
      <div class="cuebiq-panel__body ">
        <div class="row">
          <div class="col-xs-12">
            <div data-pdf-generator-reference="brand-affinity-table">
              <div>
                <div class="brand-affinity">
                  <div class="row">
                    <div class="col-xs-12">
                      <div class="likely-buttons">
                        <div>Choose the affinity:</div>
                        <button type="button" name="button" class="btn more" :class="{'active': withUplift}" v-on:click="$refs.audienceAnalysisAffinity.filter(filterBrandsWithUplift); withUplift = true;">
                          <i class="fa fa-thumbs-up" aria-hidden="true"></i>More likely
                        </button>
                        <button type="button" name="button" class="btn less" :class="{'active': !withUplift}" v-on:click="$refs.audienceAnalysisAffinity.filter(filterBrandsWithoutUplift); withUplift = false;">
                          <i class="fa fa-thumbs-down" aria-hidden="true"></i>Less likely
                        </button>
                      </div>
                    </div>
                    <div class="col-xs-12 uplift">
                      <div class="table-search">
                        <div class="row search-box">
                          <div class="col-lg-12">
                            <label for="search"></label><input type="search" id="search" placeholder="Search" class="form-control" style="margin-bottom: 15px;" v-on:keyup="$refs.audienceAnalysisAffinity.filterText(searchFor)" v-model="searchFor">
                          </div>
                        </div>
                        <div>
                          <dc-data-table
                            ref="audienceAnalysisAffinity"
                            :csv="audience_analysis_affinity.csv"
                            :name="audience_analysis_affinity.name"
                            :columns="audience_analysis_affinity.columns"
                            :tableClass="audience_analysis_affinity.class"
                            :sortFunction="audience_analysis_affinity.sortFunction"
                            :dimensionFunction="audience_analysis_affinity.dimensionFunction"
                            :textDimensionFunction="audience_analysis_affinity.textDimensionFunction"
                            :maxFunction="audience_analysis_affinity.maxFunction"
                            :filterFunction="audience_analysis_affinity.filterFunction"
                            :searchFor="searchFor"
                            wrapMaxColumn="Uplift">
                          </dc-data-table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--
    <hr class="cuebiq-separator">
    <div class="audience-brandaffinity">
      <div class="balloon__link">
        <div class="balloon balloon--top-position-mod">
          <button class="balloon__link">
            <i class="fa fa-arrow-down margin-r-10"></i>
            <span class="balloon__link-text">Request look-alike audience segments</span>
          </button>
        </div>
      </div>
      <div class="overlay audience-segments-overlay">
        <div class="inner">
          <h2>Request look-alike audience segments</h2>
          <div class="content"><button class="close" type="button" name="button"></button>
            <form>
              <div class="form-group">
                <label>Name</label>
                <input class="form-control" placeholder="Enter name" required="" value="">
              </div>
              <div class="form-group">
                <label>Email</label>
                <input type="email" class="form-control" placeholder="Enter email" required="" value="">
              </div>
              <div class="form-group">
                <label>Request</label>
                <textarea class="form-control" rows="3" required=""></textarea>
              </div>
              <button type="submit" class="btn btn-primary">Request</button>
            </form>
          </div>
        </div>
      </div>
    </div>
    -->
  </div>
</template>

<script>
import HoriBarChart from '../HoriBarChart';
import DrilldownHoriBarChart from '../DrilldownHoriBarChart';
import DcDataTable from '../DcDataTable';
import DcNumber from '../DcNumber';
import WordCloud from '../WordCloud';
import MappedTreeData from '@/modules/MappedTreeData';
import dc from 'dc';
import VueCircle from 'vue2-circle-progress'
import utils from '@/modules/utils';
import InfoTooltip from '../widgets/InfoTooltip';

let crossfilter = dc.crossfilter;

  /* eslint-disable */
  export default {
    name: 'audience-analysis',
    components: {
      InfoTooltip,
      HoriBarChart,
      DcDataTable,
      WordCloud,
      DrilldownHoriBarChart,
      DcNumber,
      VueCircle
    },

    data: function() {
      return {
        audience_analysis_affinity: {
          csv: "audience_analysis_affinity.csv",
          name: "audience_analysis_affinity",
          class: "table cuebiq brand table brand-affinity-data-table",
          sortFunction: function(d) { return +d["uplift"]; },
          dimensionFunction: function(d) { return parseFloat(d["uplift"]); },
          textDimensionFunction: function(d) { return d["category"]; },
          maxFunction: function(d) { return d["uplift"]; },
          filterFunction: function(d) { if (d !== 0.0) return d; },
          columns: [ 'category',
                    { label: 'Brand', format: this.formatBrand },
                    { label: 'Interest', format: this.formatInterest },
                    { label: 'Uplift', format: this.formatUplift } ]
        },
        filterBrandsWithUplift: function(d) { if (d !== 0.0) return d; },
        filterBrandsWithoutUplift: 0,
        searchFor: "",
        withUplift: true,
        top_carriers_chart: {
          data: null,
          options: null
        },
        top_device_models_chart: {
          data: null,
          options: null
        },
        audience_correlation_word_cloud: {
          data: null,
          keyField: 'name',
          valueField: 'uplift'
        },
        audience_number_display: null,
        audience_interest_number_display: null,
        audience_uplift_number_display: null,
        audience_chart: {
          mappedTreeData: null, // the whole data
          path: [], // the path along the tree to the currently displayed data
          options: null
        },
      };
    },
    beforeRouteLeave(to, from, next) {
      this.$splashScreen.tabEnabled = true;
      next();
    },
    mounted: function() {
      this.$splashScreen.tabEnabled = false;
      this.$cursorSpinner.processing = false;
      this.$nextTick(() => {
        this.fetchData();
        this.$_am.logEvent("visit",this.$route.name,"audience-analysis",new Date().toISOString())
      });
    },

    computed: {},
    methods: {
      fetchData: function() {
        var self = this;

        self.$dataStore.csv("audience_analysis_anagraphic.csv", function(csv) {
          var topCarriersData = csv.filter(function(d) {
            return d["type"] === "carrier";
          });

          var topDeviceModelsData = csv.filter(function(d) {
            return d["type"] === "devicemodel";
          });

          self.loadAudienceCarriersDevicesChart(topCarriersData, function(chartData, chartOptions) {
            self.top_carriers_chart.data = chartData;
            self.top_carriers_chart.options = chartOptions;
          });

          self.loadAudienceCarriersDevicesChart(topDeviceModelsData, function(chartData, chartOptions) {
            self.top_device_models_chart.data = chartData;
            self.top_device_models_chart.options = chartOptions;
          });
        });

        self.loadWordCloud();

        self.$dataStore.json("audience_analysis_geo_tree.json", function(json) {
          self.loadAudienceChart(json, function(chartMappedTreeData, chartOptions) {
            self.audience_chart.mappedTreeData = chartMappedTreeData;
            self.audience_chart.options = chartOptions;
          });
        });
      },
      formatUplift: function(d) {
        return parseFloat(d['uplift']);
      },
      formatBrand: function(d) {
        var brand = d['brand'];
        var res = brand.split("/");
        if (res.length > 1) {
          // This is a uri
          var img = res[res.length - 1]
          // Yes, the + is replaced by a space for reasons in the final url.
          var formattedImg = img.replace(/\+/g, ' ')
          var encodedImg = encodeURIComponent(formattedImg)
          return '<img alt src="' + brand.replace(img, encodedImg) + '"/>'
        } else if (res === 1) {
          //In this case it's the actual brand string
          return res;
        }
        //Not sure what this is.
        //If we fall here, anything goes for how the table will look like.
        return brand;
      },
      formatInterest: function(d) { return d3.format('.2f')(d['overlap'] * 100) + '%'; },
      loadAudienceCarriersDevicesChart: function(data, cb) {
        var self = this;

        data.sort(function(a, b) {
          return +b["exposedPercentage"] - +a["exposedPercentage"];
        });

        var maxValue = data.reduce(function(curMax, d) {
          return Math.max(curMax, Math.max(+d["exposedPercentage"], +d["convertedPercentage"]));
        }, 0) * 100;

        var labels = data.map(function(d) {
          return d["label"];
        });

        var horizontalBarChartData = {
          labels: labels,
          datasets: [{
            label: 'Exposed',
            backgroundColor: "rgb(61, 103, 154)",
            data: data.map(function(d) { return (+d["exposedPercentage"] * 100) })
          }, {
            label: 'Converted',
            backgroundColor: "rgb(110, 188, 228)",
            data: data.map(function(d) { return (+d["convertedPercentage"] * 100); })
          }]
        };

        var options = {
          responsive: true,
          maintainAspectRatio:false,
          legend: {
            display: false
          },
          title: {
            display: false
          },
          scales: {
            yAxes: [{
              barPercentage: 1,

              afterTickToLabelConversion: function(q) {
                for (var i = 0; i < q.ticks.length; i++) {
                  q.ticks[i] = utils.format.truncate(q.ticks[i], 18);
                }
              }
            }],
            xAxes: [{
              ticks: {
                min: 0,
                max: Math.ceil(maxValue/10) * 10
              },

              afterTickToLabelConversion: function(q) {
                for (var i = 0; i < q.ticks.length; i++) {
                  q.ticks[i] += '%';
                }
              }
            }]
          },

          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                var datasetLabel = data.datasets[tooltipItem.datasetIndex].label;
                return datasetLabel + ": " + tooltipItem.xLabel.toFixed(1) + "%";
              },
              title: function(tooltipItem, data) {
                return data.labels[tooltipItem[0].index];
              }
            },
            displayColors: false
          }
        };

        cb(horizontalBarChartData, options);

        self.$reportSettings.setVisibility();
      },

      loadWordCloud: function() {
        var self = this;

        self.$dataStore.csv("audience_analysis_geo_data.csv", function(csv) {
          self.audience_correlation_word_cloud.data = csv;

          var wordCloudData = crossfilter(csv);
          var dimension = wordCloudData.dimension(dc.pluck("name"));

          self.audience_interest_number_display = {
            dimension: dimension,
            crossfilterGroup: dimension.group().reduceSum(dc.pluck("overlap")),
            onChange: function(d) {
              if (self.$refs.audienceInterestRateProgress) {
                self.$refs.audienceInterestRateProgress.updateProgress(d.data() * 100);
              }
            }
          };

          self.audience_uplift_number_display = {
            dimension: dimension,
            crossfilterGroup: dimension.group().reduceSum(dc.pluck("uplift")),
            onChange: function(d) {
              if (self.$refs.audienceUpliftRateProgress) {
                self.$refs.audienceUpliftRateProgress.updateProgress(d.data() * 100);
              }
            }
          };

          self.audience_number_display = {
            numberFormat: '.0%',
            dcGroup: 'audience-correlation-word-cloud',
            secondary: {value: 0, text: 'No Effect'},
            filter: wordCloudData.dimension(dc.pluck('name')).filter // Used to filter since dc.js charts don't filter on its own dimensions
          };

          Object.assign(self.audience_interest_number_display, self.audience_number_display);
          Object.assign(self.audience_uplift_number_display, self.audience_number_display);

          self.$reportSettings.setVisibility();
        });
      },

      loadAudienceChart: function(data, cb) {
        var options = {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          title: {
            display: false
          },
          scales: {
            yAxes: [{
              barPercentage: 1,

              afterTickToLabelConversion: function(q) {
                for (var i = 0; i < q.ticks.length; i++) {
                  q.ticks[i] = utils.format.truncate(q.ticks[i], 18);
                }
              },

              afterFit: function(scaleInstance) {
                scaleInstance.width = 135;
              }
            }],
            xAxes: [{
              ticks: {
                min: 0,
                max: 100
              },

              afterTickToLabelConversion: function(q) {
                for (var i = 0; i < q.ticks.length; i++) {
                  q.ticks[i] += '%';
                }
              }
            }]
          },

          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                return tooltipItem.xLabel.toFixed(1) + "%";
              },
              title: function(tooltipItem, data) {
                return data.labels[tooltipItem[0].index];
              }
            },
            displayColors: false
          }
        };

        cb(new MappedTreeData(data), options);
      },

      onWordCloudClick: function(newWord, oldWord) {
        var self = this;

        if (newWord === oldWord)
          return;

        self.updateAudienceChartPath(newWord);

        if (self.audience_number_display) {
          self.audience_number_display.filter(newWord);
          dc.redrawAll(self.audience_number_display.dcGroup);
        }
      },

      updateAudienceChartPath: function(newPath) {
        var self = this;

        if (!Array.isArray(newPath))
          newPath = [newPath];

        self.audience_chart.path = newPath;
      },

      popAudienceChartPath: function() {
        var self = this;
        self.audience_chart.path.pop();
      }
    }
  };
</script>

<style lang="scss">
  @import '../../assets/css/tabs/audience-analysis.scss';
</style>



// WEBPACK FOOTER //
// src/components/tabs/AudienceAnalysis.vue